import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, CalendarRange, Info, Download, TrendingDown, Tags, Boxes,
  ChevronLeft, ChevronRight, ArrowDown, ArrowUp, Minus, AlertTriangle
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const PAGE_SIZE = 50;

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
const colLabel = (key) => {
  if (!key) return '';
  try {
    return new Date(`${key}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  } catch { return key; }
};

const WINDOWS = [
  { key: 'all', label: 'Tüm dönem' },
  { key: '90', label: 'Son 90 gün' },
  { key: '30', label: 'Son 30 gün' },
];
const ROLLS = [
  { key: 7, label: '7 günlük pencere' },
  { key: 14, label: '14 günlük pencere' },
  { key: 21, label: '21 günlük pencere' },
  { key: 30, label: '30 günlük pencere' },
];
const TABS = [
  { key: 'sizes', label: 'Ürün × Beden trendi', icon: TrendingDown, hint: 'beden sayısı azalıyor mu' },
  { key: 'brands', label: 'Marka × Ürün', icon: Tags, hint: 'ürün sayısı azalıyor mu' },
  { key: 'avail', label: 'Marka × Available Beden', icon: Boxes, hint: 'stoktaki beden azalıyor mu' },
];
const TREND_FILTERS = [
  { key: 'ALL', label: 'Tümü' },
  { key: 'DOWN', label: 'Sadece daralanlar' },
  { key: 'UP', label: 'Sadece genişleyenler' },
];

// Hücre ısı rengi — 0/boş şeffaf, arttıkça koyulaşır.
const heat = (n, max) => {
  if (!n) return { background: 'transparent', color: '#d1d5db' };
  const ratio = max > 1 ? (n - 1) / (max - 1) : 1;
  return {
    background: `rgba(79, 70, 229, ${(0.10 + 0.75 * ratio).toFixed(3)})`,
    color: ratio > 0.45 ? '#fff' : '#312e81',
  };
};

export default function High5McSizeAccumulation() {
  const { selectedBrand } = useAuth();
  const [tab, setTab] = useState('sizes');
  const [roll, setRoll] = useState(14);
  const [windowKey, setWindowKey] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [trendFilter, setTrendFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  // Available beden sekmesinin kaynağı: günlük MC yedeği (uzun tarihçe, kayan
  // pencere) veya hebiar feed'i (kesin sayım ama tarihçe 3 Ağu'da başlıyor).
  const [availSource, setAvailSource] = useState('mc');

  const isSizes = tab === 'sizes';
  const isAvail = tab === 'avail';
  const isAvailMc = isAvail && availSource === 'mc';

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const url = isSizes
        ? `${API_BASE}/api/sku-feed/mc-size-trend?window=${roll}&days=${windowKey}`
        : isAvailMc
          ? `${API_BASE}/api/sku-feed/mc-brand-size-trend?window=${roll}&days=${windowKey}`
          : isAvail
            ? `${API_BASE}/api/sku-feed/hebiar-brand-matrix?days=${windowKey}`
            : `${API_BASE}/api/sku-feed/mc-brand-matrix?granularity=day&days=${windowKey}`;
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
      setPage(0);
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, tab, roll, windowKey, availSource]);

  const columns = payload?.columns || [];
  const allRows = payload?.rows || [];
  const range = payload?.range || {};
  const W = payload?.windowSize || roll;

  // Marka satırlarında iki sticky sayı kolonu var; hangi alanın okunduğu sekmeye bağlı.
  const lead = isAvailMc
    ? {
        c2: `Son ${W}g beden`, c3: 'Ürün',
        c2Tip: `Son ${W} günde görülen farklı (ürün, beden) çifti — MC'de stok dışı hiç gelmediği için hepsi "olan" beden`,
        c3Tip: `Son ${W} günde MC'de görülen ürün sayısı`,
        rowC2: (r) => r.availLastDay, rowC3: (r) => r.productsLastDay,
        totC2: (t) => t.availLastDay, totC3: (t) => t.productsLastDay,
      }
    : isAvail
    ? {
        c2: 'Available', c3: 'Toplam beden',
        c2Tip: 'Son gün stokta olan beden adedi', c3Tip: 'Son gün feed\'deki toplam beden (stok dışı dahil)',
        rowC2: (r) => r.availLastDay, rowC3: (r) => r.sizesLastDay,
        totC2: (t) => t.availLastDay, totC3: (t) => t.sizesLastDay,
      }
    : {
        c2: 'Son gün', c3: 'Toplam',
        c2Tip: 'Son snapshot gününde MC\'de yayında olan benzersiz ürün',
        c3Tip: 'Aralıkta en az bir gün MC\'de görülen benzersiz ürün (birleşim) — günlük sayıdan büyük olur',
        rowC2: (r) => r.productsLastDay, rowC3: (r) => r.totalProducts,
        totC2: (t) => t.productsLastDay, totC3: (t) => t.productsEver,
      };

  const brands = useMemo(() => {
    if (!isSizes) return [];
    const s = new Set();
    allRows.forEach((r) => r.brand && s.add(r.brand));
    return [...s].sort();
  }, [allRows, isSizes]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (isSizes) {
        if (brandFilter !== 'ALL' && r.brand !== brandFilter) return false;
        if (trendFilter === 'DOWN' && !(r.comparable && r.delta < 0)) return false;
        if (trendFilter === 'UP' && !(r.comparable && r.delta > 0)) return false;
        if (q && !(`${r.title || ''} ${r.brand || ''} ${r.color || ''} ${r.offerId || ''}`.toLowerCase().includes(q))) return false;
      } else if (q && !String(r.brand || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allRows, search, brandFilter, trendFilter, isSizes]);

  const paged = isSizes ? rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : rows;
  const pageCount = isSizes ? Math.max(1, Math.ceil(rows.length / PAGE_SIZE)) : 1;

  const maxCell = useMemo(() => {
    let m = 1;
    paged.forEach((r) => {
      if (isSizes) Object.values(r.cells || {}).forEach((c) => { if (c.n > m) m = c.n; });
      else if (r.maxCell > m) m = r.maxCell;
    });
    return m;
  }, [paged, isSizes]);

  const exportCsv = () => {
    const head = isSizes
      ? ['Urun', 'Marka', 'Kod', 'Toplam beden', `Onceki ${W}g`, `Son ${W}g`, 'Degisim', ...columns.map((c) => c.key)]
      : isAvailMc
        ? ['Marka', `Urun (son ${W}g)`, `Olan beden (son ${W}g)`, ...columns.map((c) => c.key)]
        : isAvail
          ? ['Marka', 'Urun (son gun)', 'Available beden', 'Toplam beden', ...columns.map((c) => c.key)]
          : ['Marka', 'Son gun urun', 'Toplam urun', ...columns.map((c) => c.key)];
    const lines = rows.map((r) => {
      const cells = columns.map((c) => (r.cells?.[c.key]?.n ?? ''));
      const head3 = isSizes
        ? [r.title || '', r.brand || '', r.offerId || '', r.totalSizes, r.prev, r.recent, r.delta]
        : isAvailMc
          ? [r.brand || '', r.productsLastDay, r.availLastDay]
          : isAvail
            ? [r.brand || '', r.productsLastDay, r.availLastDay, r.sizesLastDay]
            : [r.brand || '', r.productsLastDay, r.totalProducts];
      return [...head3, ...cells].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    // Marka matrislerinde en üste toplam satırı
    if (!isSizes && payload?.totals) {
      const cellVals = columns.map((c) => payload.totals.cells?.[c.key]?.n ?? 0);
      const t = isAvailMc
        ? ['TOPLAM (olan beden)', payload.totals.productsLastDay, payload.totals.availLastDay, ...cellVals]
        : isAvail
          ? ['TOPLAM (available beden)', payload.totals.productsLastDay, payload.totals.availLastDay, payload.totals.sizesLastDay, ...cellVals]
          : ['TOPLAM (benzersiz urun)', payload.totals.productsLastDay, payload.totals.productsEver, ...cellVals];
      lines.unshift(t.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    }
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${isSizes ? `beden-trendi-${W}g`
      : isAvailMc ? `marka-olan-beden-${W}g` : isAvail ? 'marka-available-beden' : 'marka-urun'}_${range.start || ''}_${range.end || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = payload?.summary || {};

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Beden / Ürün Daralma Takibi</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isSizes
                    ? `Satır = ürün, sütun = gün, hücre = o güne kadarki ${W} günde görülen farklı beden sayısı.`
                    : isAvailMc
                      ? `Satır = marka, sütun = gün, hücre = son ${W} günde o markada görülen farklı beden sayısı (günlük MC yedeği).`
                    : isAvail
                      ? 'Satır = marka, sütun = gün, hücre = o gün stokta olan beden adedi (hebiar feed).'
                      : 'Satır = marka, sütun = gün, hücre = o günkü ürün sayısı (Merchant Center).'}
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAvail && (
                <select value={availSource} onChange={(e) => setAvailSource(e.target.value)}
                        className="px-3 py-2 bg-white border rounded-xl text-sm" title="Veri kaynağı">
                  <option value="mc">MC yedeği · uzun tarihçe</option>
                  <option value="hebiar">hebiar · kesin sayım</option>
                </select>
              )}
              {(isSizes || isAvailMc) && (
                <select value={roll} onChange={(e) => setRoll(Number(e.target.value))} className="px-3 py-2 bg-white border rounded-xl text-sm">
                  {ROLLS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              )}
              <select value={windowKey} onChange={(e) => setWindowKey(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {WINDOWS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
              </select>
              <button type="button" onClick={exportCsv} disabled={loading || rows.length === 0}
                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="CSV indir">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button type="button" onClick={fetchData} disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="Yenile">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Bölümler */}
          <div className="mt-5 flex flex-wrap gap-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    active ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {t.label}
                  <span className={`text-[11px] font-normal ${active ? 'text-indigo-100' : 'text-gray-400'}`}>· {t.hint}</span>
                </button>
              );
            })}
          </div>

          {/* Tarih aralığı */}
          <div className="mt-4 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-white p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-3">
                <CalendarRange className="w-5 h-5 text-indigo-600" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-indigo-700/70 font-semibold">Veri aralığı</div>
                  <div className="text-lg font-bold text-gray-900">
                    {fmtDate(range.start)} <span className="text-gray-400 font-normal">→</span> {fmtDate(range.end)}
                  </div>
                </div>
              </div>
              <div className="h-8 w-px bg-indigo-100 hidden sm:block" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-indigo-700/70 font-semibold">Sütun</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">{fmtNumber(range.buckets)} gün</div>
              </div>
              <div className="h-8 w-px bg-indigo-100 hidden sm:block" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-indigo-700/70 font-semibold">Satır</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">{fmtNumber(rows.length)} {isSizes ? 'ürün' : 'marka'}</div>
              </div>
              {!isSizes && payload?.totals && (
                <>
                  <div className="h-8 w-px bg-indigo-100 hidden sm:block" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700/70 font-semibold">
                      {isAvailMc ? `Son ${W} gün (ürün · olan beden)`
                        : isAvail ? 'Son gün (ürün · beden · stokta)' : 'Benzersiz ürün (bedenden bağımsız)'}
                    </div>
                    <div className="text-lg font-bold text-gray-900 tabular-nums">
                      {isAvailMc ? (
                        <>
                          {fmtNumber(payload.totals.productsLastDay)} <span className="text-sm font-normal text-gray-400">ürün · </span>
                          <span className="text-emerald-600">{fmtNumber(payload.totals.availLastDay)}</span>
                          <span className="text-sm font-normal text-gray-400">
                            {' '}olan beden · ort. {payload.totals.productsLastDay
                              ? (payload.totals.availLastDay / payload.totals.productsLastDay).toFixed(1) : '0'}/ürün
                          </span>
                        </>
                      ) : isAvail ? (
                        <>
                          {fmtNumber(payload.totals.productsLastDay)} <span className="text-sm font-normal text-gray-400">ürün · </span>
                          {fmtNumber(payload.totals.sizesLastDay)} <span className="text-sm font-normal text-gray-400">beden · </span>
                          <span className="text-emerald-600">{fmtNumber(payload.totals.availLastDay)}</span>
                          <span className="text-sm font-normal text-gray-400">
                            {' '}stokta (%{payload.totals.sizesLastDay
                              ? Math.round((payload.totals.availLastDay / payload.totals.sizesLastDay) * 100) : 0})
                          </span>
                        </>
                      ) : (
                        <>
                          {fmtNumber(payload.totals.productsLastDay)} <span className="text-sm font-normal text-gray-400">son gün</span>
                          <span className="text-sm font-normal text-gray-400"> · {fmtNumber(payload.totals.productsEver)} aralıkta toplam</span>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Daralma özeti */}
          {isSizes && payload?.summary && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <TrendCard tone="red" icon={ArrowDown} label="Bedeni daralan ürün"
                value={fmtNumber(s.shrinking)} sub={`son ${W}g < önceki ${W}g`}
                active={trendFilter === 'DOWN'} onClick={() => setTrendFilter(trendFilter === 'DOWN' ? 'ALL' : 'DOWN')} />
              <TrendCard tone="gray" icon={Minus} label="Değişmeyen" value={fmtNumber(s.flat)} sub="fark yok"
                active={false} onClick={() => setTrendFilter('ALL')} />
              <TrendCard tone="emerald" icon={ArrowUp} label="Bedeni genişleyen"
                value={fmtNumber(s.growing)} sub={`son ${W}g > önceki ${W}g`}
                active={trendFilter === 'UP'} onClick={() => setTrendFilter(trendFilter === 'UP' ? 'ALL' : 'UP')} />
              <div className="rounded-xl border border-gray-100 bg-white p-3">
                <div className="text-xs text-gray-500">Toplam beden (karşılaştırılabilir {fmtNumber(s.comparable)} ürün)</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">
                  {fmtNumber(s.prevTotal)} <span className="text-gray-400 font-normal">→</span> {fmtNumber(s.recentTotal)}
                  <span className={`ml-2 text-sm ${s.recentTotal < s.prevTotal ? 'text-red-600' : s.recentTotal > s.prevTotal ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {s.recentTotal - s.prevTotal > 0 ? '+' : ''}{fmtNumber(s.recentTotal - s.prevTotal)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Yöntem */}
          {isSizes && (
            <div className="mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                MC günde ürün başına tek beden gönderdiği için “bugün kaç beden var” tek günden ölçülemez.
                Hücre = <strong>o güne kadarki {W} günde görülen farklı beden sayısı</strong> (kayan pencere).
                Sayı sağa doğru düşüyorsa ürünün bedenleri daralıyor demektir. Bu bir
                <strong> tahmin edicidir, sayım değil</strong>: {W} günde tüm bedenler denk gelmeyebilir, ürün bazında
                gürültülüdür. Kesin “şu an kaç beden stokta” için hebiar SKU feed'i şart.
              </div>
            </div>
          )}

          {/* Olan beden — MC yedeği kaynaklı, feed dönme tarihi uyarısı */}
          {isAvailMc && (
            <div className="mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                Kaynak <strong>günlük MC yedeği</strong> ({range.buckets || 0} gün). MC'ye stok dışı ürün hiç
                gönderilmediği için buradaki her beden “olan” bedendir; stok dışı zaten yok.
                Ama MC günde ürün başına <strong>tek</strong> beden verir → hücre = <strong>son {W} günde görülen
                farklı beden</strong> (kayan pencere), sayım değil <strong>tahmin</strong>.
                {payload?.reliableFrom && (
                  <>
                    {' '}🔴 <strong>{fmtDate(payload.reliableFrom)} öncesi soluk gösteriliyor:</strong> feed
                    {payload.rotationFrom ? ` ${fmtDate(payload.rotationFrom)}'e` : ''} kadar her gün aynı bedeni
                    tekrarlıyordu, pencere ~1 beden görüyordu; o tarihten sonraki ilk {W} gün de hâlâ eski günlere
                    bakıyor. Oradaki düşük sayılar daralma değil, <strong>ölçüm yokluğu</strong> — o sütunlarla
                    bugünü kıyaslama.
                  </>
                )}
              </div>
            </div>
          )}

          {/* Available beden — hebiar kaynağı ve tarihçe uyarısı */}
          {isAvail && !isAvailMc && (
            <div className={`mt-3 flex gap-3 rounded-xl border p-3 ${
              columns.length < 14 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
            }`}>
              {columns.length < 14
                ? <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                : <Info className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
              <div className={`text-xs leading-relaxed ${columns.length < 14 ? 'text-amber-900' : 'text-emerald-900'}`}>
                Kaynak <strong>hebiar SKU feed'i</strong> (beden × stok) — Merchant Center bu soruyu cevaplayamaz,
                orada her satır “in stock” ve günde ürün başına tek beden gelir.
                {columns.length < 14 && (
                  <>
                    {' '}⚠️ <strong>Tarihçe {columns.length} gün</strong> (3 Ağu 2026'da başladı; tablo 1 Tem'de silinmişti,
                    3 Ağu'da onarılıp günlük 06:15 cron'a bağlandı). Geçmişe dönük veri üretilemez —
                    trend okumak için birkaç hafta birikmesi gerekir, her gün bir sütun eklenir.
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Filtreler */}
        <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder={isSizes ? 'Ürün, marka, renk, kod ara…' : 'Marka ara…'}
              className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {isSizes && (
            <>
              <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-white border rounded-xl text-sm">
                <option value="ALL">Tüm markalar</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={trendFilter} onChange={(e) => { setTrendFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {TREND_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-500 ml-auto">
            <span>Az</span>
            <div className="flex">
              {[0.15, 0.35, 0.55, 0.75, 0.95].map((r) => (
                <span key={r} className="w-5 h-4 border border-white" style={{ background: `rgba(79, 70, 229, ${r})` }} />
              ))}
            </div>
            <span>Çok</span>
          </div>
        </div>

        {/* Matris */}
        <div className="p-0">
          {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
          {!selectedBrand?.id && <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>}
          {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}

          {!loading && !error && paged.length > 0 && (
            <div className="overflow-auto max-h-[70vh]">
              <table className="text-sm border-separate" style={{ borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[260px]">
                      {isSizes ? 'Ürün' : 'Marka'}
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[64px]"
                        style={{ left: 260 }}>
                      {isSizes ? `Önceki ${W}g` : lead.c2}
                    </th>
                    {!isSizes && (
                      <th className="sticky top-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[76px]"
                          style={{ left: 324 }} title={lead.c3Tip}>
                        {lead.c3}
                      </th>
                    )}
                    {isSizes && (
                      <>
                        <th className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[58px]"
                            style={{ left: 324 }}>
                          Son {W}g
                        </th>
                        <th className="sticky top-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[56px]"
                            style={{ left: 382 }}>
                          Δ
                        </th>
                      </>
                    )}
                    {columns.map((c) => (
                      <th key={c.key} title={fmtDate(c.key)}
                          className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-[11px] font-semibold text-gray-500 whitespace-nowrap min-w-[46px]">
                        {colLabel(c.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* TOPLAM — bedenden bağımsız benzersiz ürün sayısı, gün gün */}
                  {!isSizes && payload?.totals && (
                    <tr className="font-semibold">
                      <td className="sticky left-0 z-20 bg-indigo-50 border-b border-r border-indigo-200 px-3 py-2 min-w-[260px]">
                        <div className="text-gray-900">
                          {isAvailMc ? 'TOPLAM · olan beden' : isAvail ? 'TOPLAM · available beden' : 'TOPLAM · benzersiz ürün'}
                        </div>
                        <div className="text-[10px] text-gray-500 font-normal">
                          {isAvailMc
                            ? `son ${W} günlük pencere · ${fmtNumber(payload.totals.productsLastDay)} üründe ${fmtNumber(payload.totals.availLastDay)} beden görüldü`
                            : isAvail
                              ? `${fmtNumber(payload.totals.productsLastDay)} ürün · ${fmtNumber(payload.totals.sizesLastDay)} bedenin ${fmtNumber(payload.totals.availLastDay)}'i stokta`
                              : `bedenden bağımsız · ${fmtNumber(payload.totals.productsEver)} farklı ürün aralıkta görüldü`}
                        </div>
                      </td>
                      <td className="sticky z-10 bg-indigo-50 border-b border-indigo-200 px-2 py-2 text-center" style={{ left: 260 }}>
                        <span className="inline-flex items-center justify-center min-w-[30px] px-2 py-0.5 rounded-full bg-indigo-600 text-white font-bold tabular-nums">
                          {fmtNumber(lead.totC2(payload.totals))}
                        </span>
                      </td>
                      <td className="sticky z-10 bg-indigo-50 border-b border-r border-indigo-200 px-2 py-2 text-center tabular-nums text-gray-900"
                          style={{ left: 324 }} title={lead.c3Tip}>
                        {fmtNumber(lead.totC3(payload.totals))}
                      </td>
                      {columns.map((c) => {
                        const cell = payload.totals.cells?.[c.key];
                        const n = cell?.n || 0;
                        return (
                          <td key={c.key}
                              title={isAvail
                                ? `${fmtDate(c.key)} · ${fmtNumber(n)}/${fmtNumber(cell?.s || 0)} beden stokta · ${fmtNumber(cell?.p || 0)} ürün`
                                : `${fmtDate(c.key)} · ${fmtNumber(n)} benzersiz ürün · ${cell?.s || 0} marka`}
                              className="border-b border-indigo-200 px-1 py-2 text-center tabular-nums text-xs font-bold cursor-default"
                              style={heat(n, payload.totals.maxCell || 1)}>
                            {n || '·'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {paged.map((r) => {
                    const key = isSizes ? r.productId : r.brand;
                    const down = isSizes && r.comparable && r.delta < 0;
                    const up = isSizes && r.comparable && r.delta > 0;
                    return (
                      <tr key={key} className="hover:bg-indigo-50/20">
                        <td className="sticky left-0 z-20 bg-white border-b border-r border-gray-100 px-3 py-1.5 min-w-[260px]">
                          {isSizes ? (
                            <div className="flex items-center gap-2">
                              {r.image ? (
                                <img src={r.image} alt="" loading="lazy" className="w-8 h-8 rounded object-cover border border-gray-100 bg-white shrink-0" />
                              ) : <div className="w-8 h-8 rounded bg-gray-100 shrink-0" />}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-800 truncate max-w-[190px] text-xs" title={r.title || ''}>{r.title || r.offerId}</div>
                                <div className="text-[10px] text-gray-400 truncate max-w-[190px]">
                                  {r.brand || ''}{r.brand && r.offerId ? ' · ' : ''}{r.offerId} · toplam {r.totalSizes}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="font-medium text-gray-800">{r.brand}</div>
                              {isAvailMc ? (
                                <div className="text-[10px] text-gray-400">
                                  {fmtNumber(r.productsLastDay)} ürün · ort. {r.productsLastDay ? (r.availLastDay / r.productsLastDay).toFixed(1) : '0'} beden
                                </div>
                              ) : isAvail && (
                                <div className="text-[10px] text-gray-400">
                                  {fmtNumber(r.productsLastDay)} ürün · %{r.sizesLastDay ? Math.round((r.availLastDay / r.sizesLastDay) * 100) : 0} dolu
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="sticky z-10 bg-white border-b border-gray-100 px-2 py-1.5 text-center tabular-nums text-xs text-gray-500" style={{ left: 260 }}>
                          {isSizes ? (r.comparable ? r.prev : '—') : (
                            <span className="inline-flex items-center justify-center min-w-[30px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-bold">
                              {fmtNumber(lead.rowC2(r))}
                            </span>
                          )}
                        </td>
                        {!isSizes && (
                          <td className="sticky z-10 bg-white border-b border-r border-gray-100 px-2 py-1.5 text-center tabular-nums text-xs font-semibold text-gray-800"
                              style={{ left: 324 }}
                              title={isAvail
                                ? `${r.brand} · son gün ${fmtNumber(r.productsLastDay)} ürün · ${fmtNumber(r.sizesLastDay)} bedenin ${fmtNumber(r.availLastDay)}'i stokta`
                                : `${r.brand} · aralıkta ${fmtNumber(r.totalProducts)} benzersiz ürün · ${fmtNumber(r.totalSizes)} farklı beden · ${r.daysSeen} gün görüldü`}>
                            {fmtNumber(lead.rowC3(r))}
                          </td>
                        )}
                        {isSizes && (
                          <>
                            <td className="sticky z-10 bg-white border-b border-gray-100 px-2 py-1.5 text-center tabular-nums text-xs font-semibold text-gray-800" style={{ left: 324 }}>
                              {r.recent}
                            </td>
                            <td className="sticky z-10 bg-white border-b border-r border-gray-100 px-2 py-1.5 text-center" style={{ left: 382 }}>
                              {r.comparable ? (
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold tabular-nums ${
                                  down ? 'bg-red-100 text-red-700' : up ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {down ? <ArrowDown className="w-3 h-3" /> : up ? <ArrowUp className="w-3 h-3" /> : null}
                                  {r.delta > 0 ? '+' : ''}{r.delta}
                                </span>
                              ) : <span className="text-[11px] text-gray-300">yeni</span>}
                            </td>
                          </>
                        )}
                        {columns.map((c) => {
                          const cell = r.cells?.[c.key];
                          const n = cell?.n || 0;
                          const stale = isAvailMc && payload?.reliableFrom && c.key < payload.reliableFrom;
                          const tip = isSizes
                            ? (n ? `${fmtDate(c.key)} · son ${W} günde ${n} farklı beden (${cell.o} snapshot)` : `${fmtDate(c.key)} · veri yok`)
                            : isAvailMc
                              ? (cell
                                  ? `${fmtDate(c.key)} · son ${W} günde ${n} olan beden · ${cell.p} ürün · ort. ${(n / (cell.p || 1)).toFixed(1)}`
                                    + (stale ? ' ⚠️ feed o tarihte beden döndürmüyordu, olduğundan az' : '')
                                  : `${fmtDate(c.key)} · veri yok`)
                            : isAvail
                              ? (cell ? `${fmtDate(c.key)} · ${n}/${cell.s} beden stokta (%${cell.s ? Math.round((n / cell.s) * 100) : 0}) · ${cell.p} ürün` : `${fmtDate(c.key)} · veri yok`)
                              : (n ? `${fmtDate(c.key)} · ${n} ürün` : `${fmtDate(c.key)} · ürün yok`);
                          return (
                            <td key={c.key} title={tip}
                                className={`border-b border-gray-100 px-1 py-1.5 text-center tabular-nums text-xs font-semibold cursor-default${stale ? ' opacity-40 italic' : ''}`}
                                style={heat(n, maxCell)}>
                              {/* Available sekmesinde 0 anlamlı (tüm bedenleri stok dışı) — "veri yok"tan ayrılmalı */}
                              {isAvail ? (cell ? <span className={n ? '' : 'text-red-500'}>{n}</span> : '·') : (n || '·')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && selectedBrand?.id && rows.length === 0 && (
            <div className="p-8 text-center text-gray-500">Eşleşen satır yok.</div>
          )}

          {isSizes && !loading && rows.length > PAGE_SIZE && (
            <div className="p-3 border-t bg-gray-50/60 flex items-center justify-between text-sm">
              <span className="text-xs text-gray-500">
                {fmtNumber(page * PAGE_SIZE + 1)}–{fmtNumber(Math.min((page + 1) * PAGE_SIZE, rows.length))} / {fmtNumber(rows.length)} ürün
                <span className="text-gray-400"> · en çok daralandan başlar</span>
              </span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white border rounded-lg text-xs disabled:opacity-40">
                  <ChevronLeft className="w-3.5 h-3.5" /> Önceki
                </button>
                <span className="text-xs text-gray-500 tabular-nums">{page + 1} / {pageCount}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white border rounded-lg text-xs disabled:opacity-40">
                  Sonraki <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function TrendCard({ tone, icon: Icon, label, value, sub, active, onClick }) {
  const tones = {
    red: 'text-red-700 bg-red-50 border-red-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    gray: 'text-gray-600 bg-gray-50 border-gray-200',
  };
  return (
    <button type="button" onClick={onClick}
      className={`text-left rounded-xl border p-3 transition ${tones[tone]} ${active ? 'ring-2 ring-offset-1 ring-gray-800/40' : 'hover:shadow-sm'}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-[11px] opacity-70">{sub}</div>
    </button>
  );
}
